/**
 * The document categories we always ask for during a confirmatory due diligence.
 * Titles and hints live in translations under `rfi.categories.<id>`.
 */
export const RFI_CATEGORY_IDS = [
  'commercial-plan',
  'org-chart-roadmap',
  'dev-process',
  'architecture',
  'ticket-export',
  'usage-analytics',
  'cloud-bills',
  'compliance',
] as const;

export type RfiCategoryId = (typeof RFI_CATEGORY_IDS)[number];

/** Leaf under a tenant that holds RFI documents, one folder per category. */
export const RFI_SEGMENT = 'rfi';

export function rfiPrefix(tenantId: string): string {
  return `${tenantId}/${RFI_SEGMENT}/`;
}

export function rfiCategoryPrefix(
  tenantId: string,
  categoryId: RfiCategoryId,
): string {
  return `${rfiPrefix(tenantId)}${categoryId}/`;
}

/**
 * `{tenant}/rfi/{category}/{filename}`. The category folder is
 * the category's id (e.g. `commercial-plan`), not its translated title, so the
 * key never changes with locale or copy edits.
 */
export function rfiKey(
  tenantId: string,
  categoryId: RfiCategoryId,
  filename: string,
): string {
  return `${rfiCategoryPrefix(tenantId, categoryId)}${filename}`;
}

/** The category folder a key sits in, or `null` for a key outside any known one. */
export function categoryIdFromKey(
  tenantId: string,
  key: string,
): RfiCategoryId | null {
  const prefix = rfiPrefix(tenantId);
  if (!key.startsWith(prefix)) return null;
  const [folder, ...rest] = key.slice(prefix.length).split('/');
  if (!rest.length) return null;
  return (RFI_CATEGORY_IDS as readonly string[]).includes(folder!)
    ? (folder as RfiCategoryId)
    : null;
}

const METADATA_SUFFIX = '.metadata.json';

/**
 * The ingestion pipeline writes `<file>.metadata.json` next to every upload as
 * a filter helper for the knowledge base. It is not a document the user gave us.
 */
export function isMetadataSidecar(key: string): boolean {
  return key.endsWith(METADATA_SUFFIX);
}
